/**
 * InvoicedPaymentOverview · iter303ak-fix37 — Invoiced · T10 · Action 10
 * ────────────────────────────────────────────────────────────────────────
 * Read-only per-child payment summary. Sources:
 *   • Full invoice amount    ← child's quote-items base + tagged extras
 *                              (calls /api/appraisals/{child}/contractor-overview
 *                              + parent.work_execution.extras filtered by
 *                              extras_contractor_tags[extra.id].child_id)
 *   • Deposit                ← parent.per_child_deposit_pick[child].amount
 *                              (captured at Accepted T10). Amber banner if missing.
 *   • Due                    = Full − Deposit
 *   • Due date               ← child.invoice_due_date OR
 *                              parent.payment_due_days offset from today.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Receipt, AlertTriangle, DollarSign, Calendar, CheckCircle2 } from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const fmt = (n) => Number(n || 0).toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function InvoicedPaymentOverview({ action, capturedData, onNext, onBack, onCancel, canBack, value, onChange }) {
  const params = action.params || {};
  const parentId = capturedData?.appraisal_id;
  const parent = useMemo(() => (
    capturedData?.appraisal_record || capturedData?.appraisal || {}
  ), [capturedData]);

  const acceptedChildren = useMemo(() => {
    const decisions = parent.child_decisions || {};
    return Object.entries(decisions)
      .filter(([, d]) => d?.decision === 'accepted')
      .map(([child_id, d]) => ({ child_id, ...d }));
  }, [parent]);

  const depositPick = useMemo(() => (parent.per_child_deposit_pick || {}), [parent]);
  const extras = useMemo(() => (parent.work_execution?.extras || []), [parent]);
  const extrasTags = useMemo(() => (parent.extras_contractor_tags || {}), [parent]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ack, setAck] = useState(!!(value?.acknowledged ?? false));

  const dueDaysDefault = Number(parent.payment_due_days || 14);

  const load = useCallback(async () => {
    if (!acceptedChildren.length) { setRows([]); setLoading(false); return; }
    setLoading(true);
    const list = [];
    for (const c of acceptedChildren) {
      let base = 0;
      let child_doc = null;
      try {
        const { data: overview } = await api.get(`/appraisals/${c.child_id}/contractor-overview`);
        // Sum contractor-shares + labour-shares. For client-facing invoice
        // amount, sum the full line totals (base + labour + markup).
        for (const sec of (overview?.sections || [])) {
          for (const ln of (sec?.lines || [])) {
            if (ln?.excluded_from_report) continue;
            // Prefer explicit `client_line_total` if it exists; else materials + contractor_share.
            base += Number(ln?.client_line_total || (Number(ln?.materials_total || 0) + Number(ln?.contractor_share || 0)) || 0);
          }
        }
      } catch { /* silent — 0 base */ }
      try {
        const { data } = await api.get(`/appraisals/${c.child_id}`);
        child_doc = data || null;
      } catch { /* silent */ }
      // Tagged extras for this child.
      let tagged_extras = 0;
      for (const ex of extras) {
        const tag = extrasTags[ex.id] || {};
        if (String(tag.child_id || '') === String(c.child_id)) {
          tagged_extras += Number(ex.cost || 0);
        }
      }
      const full = Number((base + tagged_extras).toFixed(2));
      const dep = Number((depositPick[c.child_id]?.amount || 0));
      const due_amount = Number((full - dep).toFixed(2));
      const invoiceDueDate = child_doc?.invoice_due_date || '';
      // Auto-compute due date if child doesn't have one.
      const computed = (() => {
        if (invoiceDueDate) return invoiceDueDate;
        const t = new Date();
        t.setDate(t.getDate() + dueDaysDefault);
        return t.toISOString().slice(0, 10);
      })();
      list.push({
        child_id:      c.child_id,
        appraisal_no:  c.appraisal_no,
        qtype_ref:     c.quote_type_ref,
        variant_label: c.variant_label,
        base:          Number(base.toFixed(2)),
        tagged_extras: Number(tagged_extras.toFixed(2)),
        full,
        deposit:       dep,
        due_amount,
        due_date:      computed,
        deposit_missing: !dep,
      });
    }
    setRows(list);
    setLoading(false);
  }, [acceptedChildren, depositPick, extras, extrasTags, dueDaysDefault]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (onChange) onChange({ acknowledged: ack, rows });
  }, [ack, rows]);  // eslint-disable-line react-hooks/exhaustive-deps

  const totals = useMemo(() => {
    const t = { full: 0, deposit: 0, due: 0 };
    for (const r of rows) {
      t.full += r.full; t.deposit += r.deposit; t.due += r.due_amount;
    }
    return t;
  }, [rows]);

  const missingDeposits = rows.filter(r => r.deposit_missing);

  return (
    <div className="space-y-4" data-testid="wf-invoiced-overview">
      <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-4">
        <div className="flex items-start gap-3">
          <Receipt className="w-5 h-5 mt-0.5 shrink-0 text-indigo-700" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-indigo-900">Payment Details Overview</div>
            <div className="text-sm mt-1 text-indigo-800">
              {params.prompt || 'Read-only per-child summary. Deposits come from the Accepted T10 capture — amber banner flags any missing.'}
            </div>
          </div>
        </div>
      </div>

      {missingDeposits.length > 0 && (
        <div className="p-3 bg-amber-50 border border-amber-300 rounded"
          data-testid="wf-invoiced-missing-deposits-banner">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
            <div className="flex-1 text-xs text-amber-900">
              <div className="font-semibold text-sm mb-1">
                {missingDeposits.length} child{missingDeposits.length === 1 ? '' : 'ren'} missing deposit capture
              </div>
              <div className="mb-1">
                Deposit values are captured at <b>Accepted → T10 · Confirm Acceptance → Action 20 (per-child deposit)</b>. If a deposit was collected but wasn't logged, head back and add it now — otherwise Due amount will be shown as = Full invoice.
              </div>
              <ul className="list-disc pl-4 mt-1">
                {missingDeposits.slice(0, 6).map(r => (
                  <li key={r.child_id}><b>{r.qtype_ref}{r.variant_label ? ` · ${r.variant_label}` : ''}</b> — A{r.appraisal_no}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {loading && <div className="p-4 text-sm text-slate-500 italic">Calculating amounts per child…</div>}

      {!loading && rows.length === 0 && (
        <div className="p-4 border border-amber-200 bg-amber-50 rounded text-sm text-amber-800">
          No accepted children on this appraisal — nothing to invoice.
        </div>
      )}

      {!loading && rows.map(r => (
        <div key={r.child_id} className={`border rounded-lg overflow-hidden ${r.deposit_missing ? 'border-amber-300 bg-amber-50/30' : 'border-slate-200 bg-white'}`}
          data-testid={`wf-invoiced-overview-row-${r.child_id}`}>
          <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-slate-800">
              {r.qtype_ref}{r.variant_label ? ` · ${r.variant_label}` : ''}
            </span>
            {r.appraisal_no && (
              <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-800 bg-indigo-100 border border-indigo-300 rounded px-1.5 py-0.5">
                A{r.appraisal_no}
              </span>
            )}
            {r.deposit_missing && (
              <span className="text-[10px] font-semibold text-amber-700 bg-amber-100 border border-amber-300 rounded px-1.5 py-0.5">
                No deposit on file
              </span>
            )}
          </div>
          <div className="p-3 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1">
                <DollarSign size={10} /> Full invoice
              </div>
              <div className="font-mono text-sm text-slate-800">${fmt(r.full)}</div>
              <div className="text-[10px] text-slate-500">
                base ${fmt(r.base)}{r.tagged_extras ? ` + extras $${fmt(r.tagged_extras)}` : ''}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1">
                <DollarSign size={10} /> Deposit paid
              </div>
              <div className="font-mono text-sm text-slate-800">${fmt(r.deposit)}</div>
              <div className="text-[10px] text-slate-500">
                {r.deposit_missing ? 'Not captured yet' : `${((r.deposit / r.full || 0) * 100).toFixed(0)}% of invoice`}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold flex items-center gap-1">
                <DollarSign size={10} /> Due
              </div>
              <div className="font-mono text-sm font-bold text-emerald-800">${fmt(r.due_amount)}</div>
              <div className="text-[10px] text-slate-500">
                Payable now
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1">
                <Calendar size={10} /> Due date
              </div>
              <div className="font-mono text-sm text-slate-800">{r.due_date}</div>
            </div>
          </div>
        </div>
      ))}

      {rows.length > 0 && (
        <div className="p-3 rounded-lg bg-slate-800 text-white flex items-center gap-6"
          data-testid="wf-invoiced-totals">
          <span className="text-[10px] uppercase tracking-widest opacity-70">Overall</span>
          <span><span className="opacity-70 text-xs">Full </span><b className="font-mono">${fmt(totals.full)}</b></span>
          <span><span className="opacity-70 text-xs">Deposit </span><b className="font-mono">${fmt(totals.deposit)}</b></span>
          <span><span className="opacity-70 text-xs">Due </span><b className="font-mono text-emerald-300">${fmt(totals.due)}</b></span>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 pt-2">
        {canBack ? <button onClick={onBack} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100">← Back</button> : <span />}
        <div className="flex gap-2 items-center">
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)}
              data-testid="wf-invoiced-overview-ack" />
            Overview acknowledged
          </label>
          {onCancel && <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100">Cancel</button>}
          <button onClick={onNext} disabled={!ack}
            data-testid="wf-invoiced-overview-continue"
            className="px-4 py-1.5 rounded-md text-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" /> Continue
          </button>
        </div>
      </div>
    </div>
  );
}
